import { U21Divider } from 'app/shared/u21-ui/components';
import { SavedFilterBar } from 'app/modules/filters/components/SavedFilterBar';
import { isEqual } from 'lodash';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import {
  Filter,
  FilterContext,
  FilterOption,
  SavedFilter,
} from 'app/modules/filters/models';
import { useSelector } from 'react-redux';
import { selectAgent } from 'app/modules/session/selectors';
import { useGetSavedFilters } from 'app/modules/filters/queries/useGetSavedFilters';
import { getValidFilters } from 'app/modules/filters/utils';

interface Props {
  appliedSavedFilter: SavedFilter | undefined;
  defaultFilters: Filter[];
  filters: Filter[];
  filterContext: FilterContext;
  onChange: (filters: Filter[]) => void;
  options: FilterOption[];
  setAppliedSavedFilter: Dispatch<SetStateAction<SavedFilter | undefined>>;
}

export const SavedFilters = ({
  appliedSavedFilter,
  defaultFilters,
  filters,
  filterContext,
  onChange,
  options,
  setAppliedSavedFilter,
}: Props) => {
  // pending applied filter held until parent's filters catch up — avoids
  // flashing dirty state in the gap between dispatch and parent re-render
  const pendingApplySavedFilterRef = useRef<SavedFilter>();

  const agent = useSelector(selectAgent);

  const { data: savedFilters = [] } = useGetSavedFilters(filterContext);

  const optionMap = useMemo(
    () =>
      options.reduce<Record<FilterOption['key'], FilterOption>>(
        (acc, i) => ({ ...acc, [i.key]: i }),
        {},
      ),
    [options],
  );

  const resolveSavedFilters = useCallback(
    (saved: Filter[]): Filter[] => {
      const valid = getValidFilters(saved, options);
      const validKeys = new Set(valid.map((i) => i.key));
      const missingRequired = defaultFilters.filter(
        (i) => optionMap[i.key]?.required && !validKeys.has(i.key),
      );
      return [...missingRequired, ...valid];
    },
    [defaultFilters, options, optionMap],
  );

  const appliedSavedFilters = useMemo(
    () =>
      savedFilters.filter((i) =>
        isEqual(resolveSavedFilters(i.filters), filters),
      ),
    [savedFilters, filters, resolveSavedFilters],
  );

  // when no applied state set + current filters match a saved filter, lock onto the first match
  const appliedSavedFilterRef = useRef(appliedSavedFilter);
  useEffect(() => {
    appliedSavedFilterRef.current = appliedSavedFilter;
  }, [appliedSavedFilter]);
  useEffect(() => {
    if (!appliedSavedFilterRef.current && appliedSavedFilters.length > 0) {
      setAppliedSavedFilter(appliedSavedFilters[0]);
    }
  }, [appliedSavedFilters, setAppliedSavedFilter]);

  // when viewing a non-owned saved filter, clear applied saved filter when
  // current filters do not match
  useEffect(() => {
    const applied = appliedSavedFilterRef.current;
    if (
      applied &&
      applied.created_by_id !== agent.id &&
      !isEqual(resolveSavedFilters(applied.filters), filters)
    ) {
      setAppliedSavedFilter(undefined);
    }
  }, [filters, agent.id, resolveSavedFilters, setAppliedSavedFilter]);

  const staleSavedFilterIds = useMemo(
    () =>
      new Set(
        savedFilters
          .filter(
            (i) =>
              getValidFilters(i.filters, options).length < i.filters.length,
          )
          .map((i) => i.id),
      ),
    [options, savedFilters],
  );

  const onApplySavedFilter = useCallback(
    (savedFilter: SavedFilter) => {
      onChange(resolveSavedFilters(savedFilter.filters));
      pendingApplySavedFilterRef.current = savedFilter;
    },
    [onChange, resolveSavedFilters],
  );

  // commit applied state once parent's filters reflect what we set —
  // avoids dirty flash between dispatch and parent re-render
  useEffect(() => {
    const pending = pendingApplySavedFilterRef.current;
    if (!pending) {
      return;
    }
    if (isEqual(resolveSavedFilters(pending.filters), filters)) {
      pendingApplySavedFilterRef.current = undefined;
      setAppliedSavedFilter(pending);
    }
  }, [filters, resolveSavedFilters, setAppliedSavedFilter]);

  const isDirty = useCallback(
    (savedFilter: SavedFilter) =>
      !isEqual(resolveSavedFilters(savedFilter.filters), filters),
    [filters, resolveSavedFilters],
  );

  return (
    <div>
      <SavedFilterBar
        appliedSavedFilter={appliedSavedFilter}
        defaultFilters={defaultFilters}
        filterContext={filterContext}
        filters={filters}
        isDirty={isDirty}
        onApplySavedFilter={onApplySavedFilter}
        onAppliedSavedFilterChange={setAppliedSavedFilter}
        onChange={onChange}
        savedFilters={savedFilters}
        staleSavedFilterIds={staleSavedFilterIds}
      />
      <U21Divider horizontal />
    </div>
  );
};
